// case-studies/pages/aitech.js
import React, { useEffect } from "react";
import { motion } from "framer-motion";
import "../styles/aitech.css";

import example1 from "../assets/aitech/aitechbanner.jpeg"; // placeholder for all images
import example2 from "../assets/aitech/token2049.jpg"; // placeholder for all images
import example3 from "../assets/aitech/aimarketplace.jpg"; // placeholder for all images
import example4 from "../assets/aitech/datacenter.jpeg"; // placeholder for all images
import example5 from "../assets/aitech/agentforge.jpeg"; // placeholder for all images

const titleVariant = {
  hidden: { opacity: 0, y: -28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const textVariant = {
  hidden: { opacity: 0, y: 28 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, ease: "easeOut" },
  },
};

const imageVariant = {
  hidden: { opacity: 0, scale: 1.18 },
  visible: {
    opacity: 1,
    scale: 1,
    transition: { duration: 0.7, ease: "easeOut" },
  },
};

const Aitech = () => {
  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: "auto",
    });
  }, []);

  return (
    <main className="ai-root">
      {/* HEADER */}
      <header className="ai-header">
        <div className="ai-header-inner">
          <div className="ai-title-block">
            <motion.h1
              className="ai-title"
              variants={titleVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.6 }}
            >
              THE COMPANY THAT FORCED ME TO OUTGROW MYSELF
            </motion.h1>

            <motion.p
              className="ai-subtitle"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.6 }}
            >
              From a Deloitte consultant to leading AI development inside a fast paced
              Web3 startup.
            </motion.p>
          </div>

          <div className="ai-meta-row">
            <motion.div
              className="ai-meta-line"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            />
            <motion.div
              className="ai-meta-items"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <div className="ai-meta-item">
                <span className="ai-meta-label">Project Type</span>
                <span className="ai-meta-value">
                  AI products, platforms and infrastructure
                </span>
              </div>
              <div className="ai-meta-item">
                <span className="ai-meta-label">Role</span>
                <span className="ai-meta-value">
                  Head of AI Development and Tech Lead
                </span>
              </div>
              <div className="ai-meta-item">
                <span className="ai-meta-label">Years</span>
                <span className="ai-meta-value">2024 - Present</span>
              </div>
            </motion.div>
          </div>
        </div>
      </header>

      {/* BANNER */}
      <section className="ai-banner">
        <div className="ai-banner-inner">
          <motion.img
            src={example1}
            alt="Solidus AI Tech and AI marketplace visual"
            className="ai-banner-image"
            variants={imageVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          />
<a
  href="https://aitech.io/"
  className="ai-main-link"
  target="_blank"
  rel="noopener noreferrer"
>
  Visit AITECH Website
</a>


        </div>
      </section>

      {/* 1. HOOK – full width like Deloitte */}
      <section className="ai-section ai-section-full">
        <div className="ai-section-inner">
          <motion.h2
            className="ai-section-title ai-section-title-center"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            The Hook
          </motion.h2>

          <motion.div
            className="ai-section-body ai-body-narrow"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <p>
              Near the end of my time at Deloitte, a colleague brought up a
              crypto project he was investing in. Solidus AI Tech. It stood out
              immediately because it actually had a{" "}
              <span className="ai-emphasis-inline">
                real world data center behind the token
              </span>
              . Not a whitepaper promise. Not a pitch deck. An actual facility
              with machines running inside it.
            </p>
            <p>
              I was curious, met the founder, liked the direction and joined the
              following month as a full stack developer. On paper my job was to
              build apps for the AI marketplace. In practice{" "}
              <span className="ai-emphasis-inline">
                I built anything the company needed
              </span>
              . A PII detection tool. Tarantula, which was the Cursor before
              Cursor. StoryBoard, one of the earliest AI image tools with real
              character consistency. A stack of productivity apps and internal
              utilities. Somewhere in the middle of all that,{" "}
              <span className="ai-emphasis-inline">
                I became completely self sufficient
              </span>{" "}
              and started shipping the workload of five people at the pace of
              one because things needed to exist quickly.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 2. CONTEXT – split, image right */}
      <section className="ai-section ai-section-split ai-split-text-left">
        <div className="ai-section-inner ai-section-inner-split">
          <motion.h2
            className="ai-section-title ai-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            Context
          </motion.h2>

          <div className="ai-split-col ai-split-col-media">
            <div className="ai-image-frame">
              <motion.img
                src={example2}
                alt="AI marketplace and conference context"
                className="ai-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="ai-split-col ai-split-col-text">
            <motion.div
              className="ai-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                The startup environment gave me a kind of freedom I had never
                felt before. Every week needed a new stack, a new tool or a new
                problem solved yesterday. There was no room for overthinking.
                Only building. It pushed me to{" "}
                <span className="ai-emphasis-inline">
                  figure out my real capabilities as a developer
                </span>{" "}
                and forced my skills to grow in every direction at once.
              </p>
              <p>
                There were surreal moments too. The company sponsored TOKEN2049
                in Singapore, the largest blockchain conference in the world. It
                happened during the F1 Singapore GP race weekend. During my time at AITECH we met people from Binance, OKX, Google, Messari, IBM and
                had dinner with{" "}
                <span className="ai-emphasis-inline">
                  Rodrigo Liang, CEO of SambaNova Systems
                </span>
                . A completely different world from consulting, with a
                completely different tempo.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. THE CHALLENGE – split, image left */}
      <section className="ai-section ai-section-split ai-split-text-right">
        <div className="ai-section-inner ai-section-inner-split">
          <motion.h2
            className="ai-section-title ai-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            The Challenge
          </motion.h2>

          <div className="ai-split-col ai-split-col-media">
            <div className="ai-image-frame">
              <motion.img
                src={example3}
                alt="Fast paced startup challenge visual"
                className="ai-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="ai-split-col ai-split-col-text">
            <motion.div
              className="ai-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
            >
              <p>
                Startups move at a pace most companies cannot survive.{" "}
                <span className="ai-emphasis-inline">
                  Requirements changed as fast as they were written
                </span>{" "}
                and features were often needed the same week they were
                suggested. There was no perfect documentation and no stable
                roadmap. The only consistent rule was simple: build something
                that works and build it quickly.
              </p>
              <p>
                APIs were built while designs were still being imagined.
                Interfaces were prototyped before backends existed. Deadlines
                overlapped nonstop. It was chaotic and messy, but it worked
                because{" "}
                <span className="ai-emphasis-inline">
                  I was willing to learn whatever the project demanded in that
                  moment
                </span>
                . New frameworks, new libraries, new cloud services. If it was
                needed, I picked it up on the fly and shipped around it.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 4. STRATEGY – full width */}
      <section className="ai-section ai-section-full">
        <div className="ai-section-inner">
          <motion.h2
            className="ai-section-title ai-section-title-center"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Strategy
          </motion.h2>

          <motion.div
            className="ai-section-body ai-body-narrow"
            variants={textVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.5 }}
          >
            <p>
              The strategy I learned was straightforward.{" "}
              <span className="ai-emphasis-inline">
                Speed matters more than polish
              </span>
              . Progress matters more than planning. If something made sense, I
              built it. If it failed, I replaced it. If it worked, I hardened it
              and scaled it. That rhythm let the company experiment quickly and
              let me ship more real products than any role I had before.
            </p>
            <p>
              It also showed me something important.{" "}
              <span className="ai-emphasis-inline">People trust builders</span>.
              When you consistently turn abstract ideas into usable products,
              teams start handing you the work that actually matters. Roadmaps,
              integrations, core flows. The more I shipped, the more ownership I
              was given.
            </p>
          </motion.div>
        </div>
      </section>

      {/* 5. OUTCOME – split, image right */}
      <section className="ai-section ai-section-split ai-split-text-left">
        <div className="ai-section-inner ai-section-inner-split">
          <motion.h2
            className="ai-section-title ai-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Outcome
          </motion.h2>

          <div className="ai-split-col ai-split-col-media">
            <div className="ai-image-frame">
              <motion.img
                src={example4}
                alt="Outcome of AI development journey"
                className="ai-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="ai-split-col ai-split-col-text">
            <motion.div
              className="ai-section-body"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
            >
              <p>
                Today I am the{" "}
                <span className="ai-emphasis-inline">
                  Head of AI Development at Solidus AI Tech
                </span>
                . I have worked across the AI marketplace, the Compute
                Marketplace, Agent Forge and several internal systems that
                support the entire ecosystem. I have also helped bring machines
                online in our{" "}
                <span className="ai-emphasis-inline">
                  8,000 square foot eco friendly HPC data center in Bucharest
                </span>
                , watching the physical side of the infrastructure come to life.
              </p>
              <p>
                All of this started because I listened to a colleague talk about
                a token and decided to take a risk. One meeting turned into a
                career shift. One project turned into leadership. One jump out
                of consulting put me in a place where I could build at a scale I
                had never imagined for myself.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 6. REFLECTION – split, image left & softer frame if you want later */}
      <section className="ai-section ai-section-split ai-section-reflection ai-split-text-right">
        <div className="ai-section-inner ai-section-inner-split">
          <motion.h2
            className="ai-section-title ai-section-title-split"
            variants={titleVariant}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Reflection
          </motion.h2>

          <div className="ai-split-col ai-split-col-media">
            <div className="ai-image-frame ai-image-frame-soft">
              <motion.img
                src={example5}
                alt="Reflection on growth and pace"
                className="ai-image"
                variants={imageVariant}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
              />
            </div>
          </div>

          <div className="ai-split-col ai-split-col-text">
            <motion.div
              className="ai-section-body ai-body-narrow"
              variants={textVariant}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.5 }}
            >
              <p>
                This chapter taught me that{" "}
                <span className="ai-emphasis-inline">
                  speed is a mindset, not a skill
                </span>
                . You do not grow by waiting for perfect conditions. You grow by
                building when things are unclear and learning as you go. Titles
                matter far less than competence. If you understand the system
                better than anyone else, people will trust you regardless of
                what your role says.
              </p>
              <p>
                It was chaotic, intense and very real. Looking back, it was
                exactly what I needed. This is the company that forced me to
                outgrow myself and showed me the kind of builder I can actually
                be.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Aitech;
